import { useEffect, useState } from "react";
import { Eye, Users, CalendarDays, TrendingUp } from "lucide-react";
import api from "@/lib/api";
import { Reveal } from "@/components/site/motion";

export default function Analytics() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/admin/analytics").then((r) => setData(r.data)).catch(() => {});
  }, []);

  const cards = data
    ? [
        { label: "Total views", value: data.total_views, icon: Eye, id: "analytics-total-views" },
        { label: "Views today", value: data.views_today, icon: CalendarDays, id: "analytics-views-today" },
        { label: "Last 7 days", value: data.views_7d, icon: TrendingUp, id: "analytics-views-week" },
        { label: "Unique visitors", value: data.unique_visitors, icon: Users, id: "analytics-unique-visitors" },
      ]
    : [];

  const maxTop = data?.top_pages?.[0]?.views || 1;

  return (
    <div data-testid="admin-analytics-page">
      <div className="mb-8">
        <h1 className="font-display text-2xl font-bold tracking-tight md:text-3xl">Analytics</h1>
        <p className="mt-1 text-sm text-neutral-500">Privacy-friendly page view tracking across your public site</p>
      </div>

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        {(data ? cards : Array(4).fill(null)).map((c, i) => (
          <Reveal key={i} delay={i * 0.06}>
            <div className="glass rounded-2xl p-5" data-testid={c?.id || `analytics-loading-${i}`}>
              {c ? (
                <>
                  <c.icon className="h-5 w-5 text-neutral-500" />
                  <p className="font-display mt-4 text-3xl font-bold">{c.value}</p>
                  <p className="mt-1 text-xs text-neutral-500">{c.label}</p>
                </>
              ) : (
                <div className="h-20 animate-pulse rounded bg-white/[0.04]" />
              )}
            </div>
          </Reveal>
        ))}
      </div>

      <div className="mt-10 grid grid-cols-1 gap-6 lg:grid-cols-2">
        <div className="glass rounded-2xl p-6" data-testid="analytics-top-pages">
          <h2 className="font-display mb-5 text-lg font-bold">Top pages</h2>
          {!data || data.top_pages.length === 0 ? (
            <p className="text-sm text-neutral-500">No visits recorded yet.</p>
          ) : (
            <div className="space-y-3">
              {data.top_pages.map((p) => (
                <div key={p.path}>
                  <div className="mb-1 flex items-center justify-between text-xs">
                    <span className="font-mono text-neutral-300">{p.path}</span>
                    <span className="text-neutral-500">{p.views} views</span>
                  </div>
                  <div className="h-1.5 overflow-hidden rounded-full bg-white/[0.06]">
                    <div className="h-full rounded-full bg-white/70" style={{ width: `${Math.max(4, (p.views / maxTop) * 100)}%` }} />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="glass rounded-2xl p-6" data-testid="analytics-recent-views">
          <h2 className="font-display mb-5 text-lg font-bold">Recent visits</h2>
          {!data || data.recent.length === 0 ? (
            <p className="text-sm text-neutral-500">No visits recorded yet.</p>
          ) : (
            <div className="space-y-2.5">
              {data.recent.map((r, i) => (
                <div key={i} className="flex items-center justify-between border-b border-white/[0.05] pb-2.5 text-sm last:border-0">
                  <span className="font-mono text-xs text-neutral-300">{r.path}</span>
                  <span className="text-xs text-neutral-500">{(r.created_at || "").replace("T", " ").slice(0, 16)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
